import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Container,
  Box,
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  Chip,
  Alert,
  CircularProgress,
  IconButton,
  Tooltip,
  TextField,
  InputAdornment,
  Grid,
  Card,
  CardContent,
} from '@mui/material';
import {
  ArrowBack as BackIcon,
  Refresh as RefreshIcon,
  Search as SearchIcon,
  Visibility as ViewIcon,
  QrCode2 as QrCodeIcon,
  Analytics as AnalyticsIcon,
  TrendingUp as TrendingUpIcon,
} from '@mui/icons-material';
import api from '../../services/api';

interface QRCode {
  id: number;
  shortcode: string;
  name: string;
  description: string | null;
  destination_url: string;
  user_id: number;
  user_email: string;
  scan_count: number;
  created_at: string;
  updated_at: string;
  last_scanned_at: string | null;
}

interface GlobalStats {
  total_qrcodes: number;
  total_scans: number;
  total_users: number;
  avg_scans_per_qrcode: string;
  most_scanned: {
    shortcode: string;
    name: string;
    scan_count: number;
  } | null;
}

const AllQRCodes: React.FC = () => {
  const navigate = useNavigate();
  const [qrcodes, setQrcodes] = useState<QRCode[]>([]);
  const [filteredQrcodes, setFilteredQrcodes] = useState<QRCode[]>([]);
  const [stats, setStats] = useState<GlobalStats | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    fetchData();
  }, []);

  useEffect(() => {
    // Filter QR codes based on search term
    if (searchTerm.trim() === '') {
      setFilteredQrcodes(qrcodes);
    } else {
      const filtered = qrcodes.filter(
        (qr) =>
          qr.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
          qr.shortcode.toLowerCase().includes(searchTerm.toLowerCase()) ||
          qr.user_email.toLowerCase().includes(searchTerm.toLowerCase()) ||
          qr.destination_url.toLowerCase().includes(searchTerm.toLowerCase())
      );
      setFilteredQrcodes(filtered);
    }
  }, [searchTerm, qrcodes]);

  const fetchData = async () => {
    setIsLoading(true);
    setError('');
    try {
      // Fetch all QR codes
      const qrResponse = await api.get('/auth/admin/all-qrcodes');

      // Fetch global stats
      const statsResponse = await api.get('/auth/admin/global-stats');

      if (qrResponse.data.success) {
        setQrcodes(qrResponse.data.data.qrcodes);
        setFilteredQrcodes(qrResponse.data.data.qrcodes);
      }

      if (statsResponse.data.success) {
        setStats(statsResponse.data.data);
      }
    } catch (err: any) {
      const errorMessage =
        err.response?.data?.error || 'Erreur lors de la récupération des données';
      setError(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  const formatDate = (dateString: string | null) => {
    if (!dateString) return 'Jamais';
    const date = new Date(dateString);
    return date.toLocaleDateString('fr-FR', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  return (
    <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box>
          <Typography variant="h4" gutterBottom fontWeight={600}>
            Tous les QR codes
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Vue globale de tous les QR codes de la plateforme avec statistiques
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Tooltip title="Actualiser">
            <IconButton onClick={fetchData} disabled={isLoading} color="primary">
              <RefreshIcon />
            </IconButton>
          </Tooltip>
          <Button variant="outlined" startIcon={<BackIcon />} onClick={() => navigate('/admin')}>
            Retour
          </Button>
        </Box>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      {/* Global Statistics */}
      {stats && (
        <Grid container spacing={3} sx={{ mb: 4 }}>
          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <Box
                    sx={{
                      bgcolor: 'primary.main',
                      borderRadius: '50%',
                      p: 1.5,
                      display: 'flex',
                    }}
                  >
                    <QrCodeIcon sx={{ color: 'white' }} />
                  </Box>
                  <Box>
                    <Typography variant="h4">{stats.total_qrcodes}</Typography>
                    <Typography variant="body2" color="text.secondary">
                      QR codes totaux
                    </Typography>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <Box
                    sx={{
                      bgcolor: 'secondary.main',
                      borderRadius: '50%',
                      p: 1.5,
                      display: 'flex',
                    }}
                  >
                    <AnalyticsIcon sx={{ color: 'white' }} />
                  </Box>
                  <Box>
                    <Typography variant="h4">{stats.total_scans}</Typography>
                    <Typography variant="body2" color="text.secondary">
                      Scans totaux
                    </Typography>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <Box
                    sx={{
                      bgcolor: 'success.main',
                      borderRadius: '50%',
                      p: 1.5,
                      display: 'flex',
                    }}
                  >
                    <TrendingUpIcon sx={{ color: 'white' }} />
                  </Box>
                  <Box>
                    <Typography variant="h4">{stats.avg_scans_per_qrcode}</Typography>
                    <Typography variant="body2" color="text.secondary">
                      Scans moyens
                    </Typography>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Box>
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    Plus scanné
                  </Typography>
                  {stats.most_scanned ? (
                    <>
                      <Typography variant="h6" noWrap>
                        {stats.most_scanned.name}
                      </Typography>
                      <Typography variant="h4" color="primary.main">
                        {stats.most_scanned.scan_count}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        scans
                      </Typography>
                    </>
                  ) : (
                    <Typography variant="body2">Aucun scan</Typography>
                  )}
                </Box>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}

      {/* Search Bar */}
      <Paper elevation={3} sx={{ p: 2, mb: 3 }}>
        <TextField
          fullWidth
          placeholder="Rechercher par nom, shortcode, utilisateur ou URL..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            ),
          }}
        />
      </Paper>

      {/* QR Codes Table */}
      <Paper elevation={3}>
        {isLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', p: 8 }}>
            <CircularProgress />
          </Box>
        ) : filteredQrcodes.length === 0 ? (
          <Box sx={{ p: 8, textAlign: 'center' }}>
            <QrCodeIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
            <Typography variant="h6" color="text.secondary" gutterBottom>
              {searchTerm ? 'Aucun QR code trouvé' : 'Aucun QR code'}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {searchTerm
                ? 'Essayez avec un autre terme de recherche'
                : 'Aucun QR code créé dans le système'}
            </Typography>
          </Box>
        ) : (
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow sx={{ backgroundColor: 'primary.main' }}>
                  <TableCell sx={{ color: 'white', fontWeight: 600 }}>Shortcode</TableCell>
                  <TableCell sx={{ color: 'white', fontWeight: 600 }}>Nom</TableCell>
                  <TableCell sx={{ color: 'white', fontWeight: 600 }}>Propriétaire</TableCell>
                  <TableCell sx={{ color: 'white', fontWeight: 600 }}>URL</TableCell>
                  <TableCell sx={{ color: 'white', fontWeight: 600 }} align="center">
                    Scans
                  </TableCell>
                  <TableCell sx={{ color: 'white', fontWeight: 600 }}>Créé le</TableCell>
                  <TableCell sx={{ color: 'white', fontWeight: 600 }}>
                    Dernier scan
                  </TableCell>
                  <TableCell align="center" sx={{ color: 'white', fontWeight: 600 }}>
                    Actions
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredQrcodes.map((qrcode) => (
                  <TableRow
                    key={qrcode.id}
                    sx={{ '&:hover': { backgroundColor: 'action.hover' } }}
                  >
                    <TableCell>
                      <Chip label={qrcode.shortcode} color="primary" size="small" />
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" fontWeight={500}>
                        {qrcode.name}
                      </Typography>
                      {qrcode.description && (
                        <Typography variant="caption" color="text.secondary">
                          {qrcode.description}
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">{qrcode.user_email}</Typography>
                    </TableCell>
                    <TableCell>
                      <Typography
                        variant="body2"
                        sx={{
                          maxWidth: 250,
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                        }}
                      >
                        {qrcode.destination_url}
                      </Typography>
                    </TableCell>
                    <TableCell align="center">
                      <Chip
                        label={qrcode.scan_count}
                        color={qrcode.scan_count > 0 ? 'success' : 'default'}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" color="text.secondary">
                        {formatDate(qrcode.created_at)}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" color="text.secondary">
                        {formatDate(qrcode.last_scanned_at)}
                      </Typography>
                    </TableCell>
                    <TableCell align="center">
                      <Tooltip title="Voir les détails">
                        <IconButton
                          size="small"
                          color="primary"
                          onClick={() => navigate(`/qrcodes/${qrcode.id}`)}
                        >
                          <ViewIcon />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Paper>

      {/* Results Summary */}
      {searchTerm && filteredQrcodes.length > 0 && (
        <Box sx={{ mt: 2 }}>
          <Typography variant="body2" color="text.secondary">
            {filteredQrcodes.length} résultat{filteredQrcodes.length > 1 ? 's' : ''} trouvé
            {filteredQrcodes.length > 1 ? 's' : ''}
          </Typography>
        </Box>
      )}
    </Container>
  );
};

export default AllQRCodes;
